/**
 * Données de la boîte de réception, partagées par la cloche et la page :
 * nombre exact de non lues (sans plafond), marquage d'un avis, « Tout marquer
 * comme lu » jusqu'à l'avis le plus récent affiché, ouverture de l'élément
 * concerné.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '../../core/api.js';
import { useAuth } from '../../core/auth.js';
import { notificationFileId, notificationTarget } from '../../core/notification-target.js';

export interface InboxNotification {
  id: string;
  event: string;
  title: string;
  body: string | null;
  ref_kind: string | null;
  ref_id: string | null;
  critical: boolean;
  read_at: string | null;
  created_at: string;
}

export interface UnreadCount {
  unread: number;
  critical: number;
  /** L'avis non lu le plus récent au moment de la lecture : repère de « Tout marquer comme lu ». */
  newestId: string | null;
}

export const INBOX_PATH = '/notifications';
export const PREFERENCES_PATH = '/profil/compte/notifications';
/** Page qu'ouvre une notification push touchée (`public/sw-push.js`). */
export const PUSH_OPEN_PATH = '/notifications/ouvrir';

/** Relit tout ce qui montre un avis : listes, cloche, compteur. */
export function refreshInbox(queryClient: QueryClient): Promise<unknown> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ['notifications'] }),
    queryClient.invalidateQueries({ queryKey: ['notifications-unread'] }),
  ]);
}

export function useUnreadCount(options: { refetchInterval?: number } = {}) {
  const { me } = useAuth();
  return useQuery({
    queryKey: ['notifications-unread', me?.id, 'count'],
    queryFn: () => api<UnreadCount>('/notifications/unread-count'),
    refetchInterval: options.refetchInterval ?? 60_000,
    retry: false,
    enabled: Boolean(me),
  });
}

/** Marquer un avis lu ou non lu ; le geste rend la main à l'écran qui l'affiche. */
export function useMarkNotification(onDone?: (input: { id: string; read: boolean }) => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; read: boolean }) =>
      api(`/notifications/${input.id}/${input.read ? 'read' : 'unread'}`, { method: 'POST' }),
    onSuccess: async (_result, input) => {
      onDone?.(input);
      await refreshInbox(queryClient);
    },
  });
}

/**
 * « Tout marquer comme lu » jusqu'à `upTo`, l'avis le plus récent affiché :
 * un avis arrivé depuis reste non lu (le serveur l'applique).
 */
export function useReadAll(onDone?: (marked: number) => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (upTo: string) =>
      api<{ marked: number; unread: number }>('/notifications/read-all', { method: 'POST', body: { upTo } }),
    onSuccess: async (result) => {
      onDone?.(result.marked);
      await refreshInbox(queryClient);
    },
  });
}

/**
 * Ouvre l'élément d'un avis (et le marque lu). Un export terminé se
 * télécharge : son adresse est lue d'abord, un fichier expiré ne fait pas
 * disparaître l'avis. Rend la page à ouvrir, ou rien.
 */
export async function openNotification(
  notification: InboxNotification,
  queryClient: QueryClient,
): Promise<string | null> {
  const markRead = async (): Promise<void> => {
    if (notification.read_at) return;
    await api(`/notifications/${notification.id}/read`, { method: 'POST' });
    await refreshInbox(queryClient);
  };
  const fileId = notificationFileId(notification);
  if (fileId) {
    const file = await api<{ url: string }>(`/files/${fileId}`);
    await markRead();
    window.location.assign(file.url);
    return null;
  }
  await markRead();
  return notificationTarget(notification);
}
